import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import { api, API } from "@/lib/api";
import { Play, Film, Download } from "lucide-react";

export default function Gallery() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/videos")
      .then(({ data }) => setVideos(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <Nav />
      <div className="mx-auto max-w-[1400px] px-6 py-12">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFE800]">// Gallery</div>
            <h1 className="font-display text-6xl leading-none">Your Videos</h1>
          </div>
          <Link to="/studio" data-testid="gallery-new-video-btn" className="btn-brutal">
            <Film size={16} /> Make Another
          </Link>
        </div>

        {loading ? (
          <div className="border border-dashed border-[#222] p-16 text-center font-mono text-xs uppercase tracking-widest text-[#808080]">
            Loading…
          </div>
        ) : videos.length === 0 ? (
          <div className="border border-dashed border-[#222] p-16 text-center">
            <div className="font-display text-4xl">Nothing here yet.</div>
            <p className="mt-3 text-[#808080]">Head to the Studio and render your first music video.</p>
            <Link to="/studio" className="btn-brutal mt-6 inline-flex" data-testid="gallery-empty-cta">
              <Play size={14} /> Open Studio
            </Link>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {videos.map((v) => (
              <div key={v.id} className="frame p-0" data-testid={`gallery-item-${v.id}`}>
                <div className="border-b border-[#222] p-4">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#808080]">
                    {new Date(v.created_at).toLocaleString()}
                  </div>
                  <div className="mt-1 font-display text-2xl leading-none">{v.title || "Untitled"}</div>
                  <div className="mt-2 flex gap-3 font-mono text-[10px] uppercase tracking-widest text-[#808080]">
                    <span>{v.scenes?.length || 0} scenes</span>
                    <span>·</span>
                    <span className={v.status === "ready" ? "text-[#00F0FF]" : v.status === "failed" ? "text-[#FF003C]" : "text-[#FFE800]"}>
                      {v.status}
                    </span>
                  </div>
                </div>
                {v.status === "ready" && v.clip_file_ids?.[0] ? (
                  <>
                    <video
                      src={`${API}/files/${v.clip_file_ids[0]}`}
                      controls
                      className="w-full"
                    />
                    <div className="flex flex-wrap gap-2 border-t border-[#222] p-3">
                      {v.clip_file_ids.map((cid, i) => (
                        <a
                          key={cid}
                          href={`${API}/files/${cid}/download`}
                          className="btn-ghost"
                          data-testid={`gallery-download-${v.id}-${i}`}
                        >
                          <Download size={12} /> MP4 {v.clip_file_ids.length > 1 ? `#${i + 1}` : ""}
                        </a>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="flex h-40 items-center justify-center bg-[#0A0A0B] font-mono text-xs text-[#808080]">
                    {v.status === "failed" ? v.error?.slice(0, 80) || "Failed" : `Rendering… ${v.progress || 0}%`}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
